/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import Landing from './pages/Landing';
import Login from './pages/Login';
import MerchantDashboard from './pages/MerchantDashboard';
import PublicApp from "./pages/PublicApp";
import { RiderApp } from "./pages/RiderApp";
import Pricing from './pages/Pricing';
import Checkout from './pages/Checkout';
import SuperAdmin from './pages/SuperAdmin';
import ScanAndGo from './pages/ScanAndGo';
import { HospitalityTableOrder, WalletPayRequest, HospitalityFoodApp, ConsumerScanGoStoreSelect, ConsumerScanGoShop, ConsumerHotelSelect, ConsumerHotelBooking, ConsumerTableSelect, ConsumerTableBooking } from './components/hospitality/HospitalityComponents';
import { ConsumerPosDisplay } from './components/retail/RetailPOSFullScreen';
import CustomerPolicy from './pages/CustomerPolicy';
import MerchantPolicy from './pages/MerchantPolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SetPassword from './pages/SetPassword';
import ConfirmLink from './pages/ConfirmLink';
import ProfessionalLogin from './pages/professional/ProfessionalLogin';
import ProfessionalDashboard from './pages/professional/ProfessionalDashboard';
import NotFound from './pages/NotFound';
import RateService from './pages/public/RateService';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { ResetPasswordModal } from './components/auth/ResetPasswordModal';

export default function App() {
  return (
    <TenantProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/confirm-link" element={<ConfirmLink />} />

            {/* Professional Portal Routes - declared before wildcards */}
            <Route path="/professional/login" element={<ProfessionalLogin />} />
            <Route path="/professional/dashboard" element={<ProfessionalDashboard />} />
            <Route path="/professional" element={<ProfessionalDashboard />} />
            <Route path="/professional/*" element={<ProfessionalDashboard />} />
            <Route path="/pro" element={<ProfessionalDashboard />} />
            <Route path="/pro/*" element={<ProfessionalDashboard />} />

            {/* Rider Portal Routes */}
            <Route path="/rider-app" element={<RiderApp />} />
            <Route path="/rider" element={<RiderApp />} />
            <Route path="/rider/*" element={<RiderApp />} />

            {/* Public Customer App Routes */}
            <Route path="/public" element={<PublicApp />} />
            <Route path="/public/*" element={<PublicApp />} />
            <Route path="/app" element={<PublicApp />} />
            <Route path="/app/*" element={<PublicApp />} />

            {/* Super Admin Routes */}
            <Route path="/superadmin" element={<SuperAdmin />} />
            <Route path="/superadmin/*" element={<SuperAdmin />} />

            {/* Service & Rating Routes */}
            <Route path="/rate/:bookingId" element={<RateService />} />
            <Route path="/scan" element={<ScanAndGo />} />
            <Route path="/scan/*" element={<ScanAndGo />} />
            <Route path="/table-order" element={<HospitalityTableOrder />} />
            <Route path="/wallet-pay" element={<WalletPayRequest />} />
            <Route path="/food" element={<HospitalityFoodApp />} />
            <Route path="/scan-go" element={<ConsumerScanGoStoreSelect />} />
            <Route path="/scan-go/shop" element={<ConsumerScanGoShop />} />
            <Route path="/stay" element={<ConsumerHotelSelect />} />
            <Route path="/pos-display" element={<ConsumerPosDisplay />} />
            <Route path="/stay/hotel" element={<ConsumerHotelBooking />} />
            <Route path="/book-table" element={<ConsumerTableSelect />} />
            <Route path="/book-table/reserve" element={<ConsumerTableBooking />} />
            <Route path="/customer-policy" element={<CustomerPolicy />} />
            <Route path="/merchant-policy" element={<MerchantPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            {/* Merchant Dashboard Routes */}
            <Route path="/merchant/staff" element={<MerchantDashboard />} />
            <Route path="/merchant/settings" element={<MerchantDashboard />} />
            <Route path="/merchant/*" element={<MerchantDashboard />} />
            <Route path="/merchant-dashboard" element={<MerchantDashboard />} />
            <Route path="/merchant-dashboard/*" element={<MerchantDashboard />} />
            <Route path="/inventory" element={<MerchantDashboard />} />
            <Route path="/reports" element={<MerchantDashboard />} />
            <Route path="/settings" element={<MerchantDashboard />} />
            <Route path="/equipment" element={<MerchantDashboard />} />
            <Route path="/billing" element={<MerchantDashboard />} />
            <Route path="/services" element={<MerchantDashboard />} />
            <Route path="/services/inward-payment" element={<MerchantDashboard />} />
            <Route path="/services/outward-payment" element={<MerchantDashboard />} />
            <Route path="/services/daily-expense" element={<MerchantDashboard />} />
            <Route path="/services/other-income" element={<MerchantDashboard />} />
            <Route path="/services/print-settings" element={<MerchantDashboard />} />
            <Route path="/merchant-dashboard/services/inward-payment" element={<MerchantDashboard />} />
            <Route path="/merchant-dashboard/services/outward-payment" element={<MerchantDashboard />} />
            <Route path="/merchant-dashboard/services/daily-expense" element={<MerchantDashboard />} />
            <Route path="/merchant-dashboard/services/other-income" element={<MerchantDashboard />} />
            <Route path="/merchant-dashboard/services/print-settings" element={<MerchantDashboard />} />
            <Route path="/services/*" element={<MerchantDashboard />} />

            {/* Standalone 404 Catch-All (Never route unknown paths to MerchantDashboard) */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <PWAInstallPrompt />
          <ResetPasswordModal />
        </BrowserRouter>
      </AuthProvider>
    </TenantProvider>
  );
}
